import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Form, Space, Steps, Typography } from 'antd';
import { ClassesStep, CoefficientsStep, MatieresStep, SousMatieresStep, SummaryStep } from './PedagogieWizardSteps';
import { KORI_ORANGE } from './pedagogie-wizard.types';
import { usePedagogieWizard } from './usePedagogieWizard';

const { Step } = Steps;

interface PedagogieWizardProps {
  ecoleId: string;
  initialCycleId?: string;
  onBack: () => void;
}

export const PedagogieWizard = ({ ecoleId, initialCycleId, onBack }: PedagogieWizardProps) => {
  const wizard = usePedagogieWizard({ ecoleId, initialCycleId, onBack });
  const commonStepProps = {
    activeCycleId: wizard.activeCycleId,
    existingCycleOptions: wizard.existingCycleOptions,
    setActiveCycleId: wizard.setActiveCycleId,
  };
  const stepContents = [
    <ClassesStep
      key="classes"
      {...commonStepProps}
      activeCycle={wizard.activeCycle}
      classeStepRows={wizard.classeStepRows}
      loading={wizard.loading}
      selectedClasseIds={wizard.selectedClasseIds}
      setClasses={wizard.setClasses}
      setSelectedClasseIds={wizard.setSelectedClasseIds}
    />,
    <MatieresStep
      key="matieres"
      {...commonStepProps}
      activeCycle={wizard.activeCycle}
      cycleMatieres={wizard.cycleMatieres}
      ecoleMatieres={wizard.ecoleMatieres}
      setCycleMatieres={wizard.setCycleMatieres}
      sousMatieres={wizard.sousMatieres}
    />,
    <SousMatieresStep
      key="sous-matieres"
      {...commonStepProps}
      cycles={wizard.cycles}
      matieresWithSousMatieres={wizard.matieresWithSousMatieres}
      setSousMatieres={wizard.setSousMatieres}
      sousMatieres={wizard.sousMatieres}
    />,
    ...(wizard.activeCycleUsesCoefficients
      ? [
          <CoefficientsStep
            key="coefficients"
            {...commonStepProps}
            activeCycle={wizard.activeCycle}
            activeCycleClasses={wizard.activeCycleClasses}
            activeCycleMatieres={wizard.activeCycleMatieres}
            activeCycleUsesCoefficients={wizard.activeCycleUsesCoefficients}
            coefficients={wizard.coefficients}
            setCoefficients={wizard.setCoefficients}
            sousMatieres={wizard.sousMatieres}
            sousCoefficients={wizard.sousCoefficients}
            setSousCoefficients={wizard.setSousCoefficients}
            coefficientSources={wizard.coefficientSources}
            setCoefficientSources={wizard.setCoefficientSources}
          />,
        ]
      : []),
    <SummaryStep
      key="summary"
      activeCycle={wizard.activeCycle}
      classesByCycle={wizard.classesByCycle}
      cycleMatieres={wizard.cycleMatieres}
      scoringModesByCycle={wizard.scoringModesByCycle}
      sousMatieres={wizard.sousMatieres}
    />,
  ];
  const stepTitles = wizard.activeCycleUsesCoefficients
    ? ['Classes', 'Matières', 'Sous-matières', 'Coefficients', 'Récapitulatif']
    : ['Classes', 'Matières', 'Sous-matières', 'Récapitulatif'];

  return (
    <Card style={{ borderRadius: 16, border: '1px solid #f3f3f3', boxShadow: '0 6px 18px rgba(0,0,0,0.05)' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start" wrap>
          <Space direction="vertical" size={4}>
            <Typography.Title level={2} style={{ margin: 0 }}>
              Création guidée de la pédagogie
            </Typography.Title>
            {wizard.activeCycle?.nom && <Typography.Text type="secondary">{wizard.activeCycle.nom}</Typography.Text>}
          </Space>
          <Button onClick={wizard.onBack} disabled={wizard.saving}>
            Retour aux cycles
          </Button>
        </Space>
        <Steps current={wizard.currentStep} size="small">
          {stepTitles.map(title => (
            <Step key={title} title={title} />
          ))}
        </Steps>
        <Form layout="vertical">{stepContents[wizard.currentStep]}</Form>
        <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
          {wizard.currentStep > 0 && (
            <Button onClick={() => wizard.setCurrentStep(step => step - 1)} disabled={wizard.saving}>
              Précédent
            </Button>
          )}
          {wizard.currentStep < wizard.lastStepIndex ? (
            <Button
              type="primary"
              loading={wizard.saving}
              onClick={wizard.goNext}
              style={{ backgroundColor: KORI_ORANGE, borderColor: KORI_ORANGE }}
            >
              Enregistrer et continuer
            </Button>
          ) : (
            <Button type="primary" onClick={wizard.onBack} style={{ backgroundColor: KORI_ORANGE, borderColor: KORI_ORANGE }}>
              Terminer
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export const PedagogieWizardPage = (props: RouteComponentProps<{ ecoleid: string; cycleId?: string }>) => {
  const { ecoleid, cycleId } = props.match.params;
  const backToConfig = () => props.history.push(`/configuration-pedagogie/${ecoleid}`);

  return <PedagogieWizard ecoleId={ecoleid} initialCycleId={cycleId} onBack={backToConfig} />;
};

export default PedagogieWizard;
